// ============================================================
//  WHITEBOARD CLIENT — Web app (runs inside the WebView)
//  Connects to /ws/whiteboard, syncs state, exposes methods.
//
//  Message protocol (client → server):
//    auth           — first message, authenticates the socket
//    whiteboard:pong — reply to server keepalive ping
//    object:preview  — in-progress stroke point (lossy)
//    object:commit   — finished stroke (durable)
//    object:delete   — soft-delete an object
//    permission:change — change drawMode (host only)
//    hand:raise / hand:lower / hand:grant
//    page:switch     — navigate to a different page
//    page:add        — add a new blank page
//    page:delete     — delete a page (server re-indexes)
//
//  Message protocol (server → client):
//    whiteboard:ping      — keepalive, must pong immediately
//    whiteboard:sync      — initial state on join/reconnect
//    whiteboard:sync-objects — chunked object list
//    whiteboard:sync-done — end of chunked sync
//    object:preview       — remote in-progress stroke
//    object:commit        — remote finished stroke
//    object:delete        — remote deletion
//    permission:change    — drawMode changed
//    hand:grant           — draw permission granted to a student
//    page:switch          — active page changed
//    page:added           — new blank page created
//    page:deleted         — page removed, pages re-indexed
//    pdf:pages-added      — PDF worksheet pages added
//    permission:denied    — action rejected by server
// ============================================================

export function createWhiteboardClient({ apiBaseUrl, taskId, applicationId, idToken, onState }) {
  // ── Connection state ──────────────────────────────────────
  let ws = null
  let tornDown = false
  let reconnectTimer = null
  let reconnectAttempts = 0
  const MAX_RECONNECT_ATTEMPTS = 5

  // ── Object store ──────────────────────────────────────────
  const objectsById = new Map()
  // FIX: Whiteboard.jsx applies erase/draw actions optimistically —
  // _localDelete removes an object from the canvas immediately,
  // before the server has confirmed the matching object:delete.
  // If the server later denies it (a draw-permission race), the
  // object needs to come back on THIS client, since it never
  // actually left the database or the other participant's board.
  // pendingDeletes remembers what was removed, keyed by objectId,
  // so the permission:denied handler below can restore it.
  const pendingDeletes = new Map()
  let onPreviewCallback = null
  let onViewportCallback = null
  let syncing = false
  // FIX — DETECT A DROPPED BROADCAST. The server numbers every
  // durable change with a monotonic per-room `seq` (see
  // whiteboard.signaling.js's publishToRoom). Redis pub/sub is
  // at-most-once, so a lost frame previously meant this board
  // stayed permanently out of step with the other participant's
  // with nothing noticing. Tracking the last seq seen turns that
  // into a detectable gap we can repair by asking for a resync.
  let lastSeq = 0
  let resyncPending = false

  // ── Shared state pushed to React via onState ──────────────
  const state = {
    connectionState: 'connecting',
    drawMode:        'everyone',
    canDraw:         false,
    pages:           [],
    currentPageIndex: 0,
    objects:         [],
    deniedReason:    null,
    identity:        null,
  }

  const push = (patch) => {
    Object.assign(state, patch)
    onState({ ...state })
  }

  const pushObjects = () => {
    state.objects = Array.from(objectsById.values())
    onState({ ...state })
  }

  // ── WebSocket ─────────────────────────────────────────────
  const wsUrl = `${apiBaseUrl.replace(/^http/, 'ws')}/ws/whiteboard`

  const send = (payload) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify(payload)) } catch { /* ignore */ }
    }
  }

  const connect = () => {
    if (tornDown) return
    ws = new WebSocket(wsUrl)

    ws.onopen = () => {
      if (tornDown) return
      send({ type: 'auth', taskId, idToken, applicationId: applicationId || undefined })
    }

    ws.onmessage = (event) => {
      if (tornDown) return
      let msg
      try { msg = JSON.parse(event.data) } catch { return }

      // FIX — GAP DETECTION. Any message carrying a `seq` is a
      // durable change the server numbered. If we receive seq N+2
      // while holding N, exactly one broadcast was lost in transit
      // and this board is now missing a stroke, an erase or a page
      // change that the other participant already has. Ask for the
      // authoritative snapshot rather than carrying on divergent.
      // resyncPending stops a burst of out-of-order frames from
      // firing several requests at once; it clears when the resync
      // snapshot arrives.
      if (typeof msg.seq === 'number') {
        if (lastSeq && msg.seq > lastSeq + 1 && !resyncPending) {
          resyncPending = true
          send({ type: 'whiteboard:resync' })
        }
        if (msg.seq > lastSeq) lastSeq = msg.seq
      }

      switch (msg.type) {

        // ── Keepalive ───────────────────────────────────────
        case 'whiteboard:ping':
          send({ type: 'whiteboard:pong', ts: msg.ts })
          return

        // ── Initial sync ────────────────────────────────────
        case 'whiteboard:sync': {
          syncing = true
          const isReconnect = state.connectionState === 'connected'
          // FIX: a resync is a repair of divergent state, so the
          // snapshot must REPLACE what we hold, not merge into it —
          // otherwise an object this client wrongly still shows (or
          // wrongly dropped) survives the repair. A plain reconnect
          // keeps its objects as before, since nothing indicated
          // they were wrong.
          if (!isReconnect || msg.resync) objectsById.clear()
          // Baseline for gap detection — see the onmessage guard.
          // Set from the server's authoritative value so the next
          // broadcast is judged against the right starting point.
          if (typeof msg.seq === 'number') lastSeq = msg.seq
          resyncPending = false
          push({
            connectionState:  'connected',
            identity:         msg.identity,
            drawMode:         msg.drawMode,
            canDraw:          isReconnect && !msg.resync
              ? state.canDraw
              : (msg.canDraw ?? msg.drawMode === 'everyone'),
            pages:            msg.pages,
            currentPageIndex: msg.currentPageIndex,
            deniedReason:     null,
          })
          // Late joiner / reconnect: land where everyone else is
          // looking rather than at the origin. Deferred so the
          // callback runs after the consumer has processed this sync.
          if (msg.viewport) {
            const v = msg.viewport
            setTimeout(() => onViewportCallback?.({ scrollX: v.scrollX, scrollY: v.scrollY, zoom: v.zoom }), 0)
          }
          reconnectAttempts = 0
          return
        }

        // Either side moving the board moves it for everyone. The
        // server never sends this back to whoever sent it, so there is
        // no self-echo to filter out.
        case 'viewport:sync':
          onViewportCallback?.({ scrollX: msg.scrollX, scrollY: msg.scrollY, zoom: msg.zoom })
          return

        case 'whiteboard:sync-objects':
          for (const obj of msg.objects) objectsById.set(obj.id, obj)
          return

        case 'whiteboard:sync-done':
          syncing = false
          pushObjects()
          return

        // ── Objects ─────────────────────────────────────────
        case 'object:preview':
          if (onPreviewCallback) {
            onPreviewCallback({
              id:          msg.id,
              pageId:      msg.pageId,
              authorId:    msg.authorId,
              tool:        msg.tool,
              color:       msg.color,
              strokeWidth: msg.strokeWidth,
              geometry:    msg.geometry,
            })
          }
          return

        case 'object:commit':
          console.log('[wb-client] received object:commit', {
            id: msg.id, authorId: msg.authorId, seq: msg.seq,
            wasAlreadyKnown: objectsById.has(msg.id),
          })
          objectsById.set(msg.id, { ...msg, __preview: false })
          pushObjects()
          return

        case 'object:delete':
          console.log('[wb-client] received object:delete', {
            objectId: msg.objectId, authorId: msg.authorId, seq: msg.seq,
            wasPresent: objectsById.has(msg.objectId),
          })
          objectsById.delete(msg.objectId)
          pushObjects()
          return

        // ── Permissions ─────────────────────────────────────
        case 'permission:change':
          push({ drawMode: msg.mode })
          return

        case 'hand:grant':
          if (msg.studentId === state.identity) push({ canDraw: true })
          return

        case 'permission:denied':
          console.log('[wb-client] received permission:denied', {
            action: msg.action, reason: msg.reason, objectId: msg.objectId, id: msg.id,
          })
          // BUG FIX — SILENT DISCARD FOR STALE REPLAYED COMMITS:
          // 'That object was deleted' is sent by commitObject() when
          // a client replays a commit for an id that was already
          // soft-deleted (the root cause of the "erase only sticks on
          // my side" bug — see whiteboard.service.js commitObject for
          // the full explanation). This is an expected, normal path
          // after the service-side fix: the commit was a stale replay,
          // the server correctly rejected it, and the object is already
          // absent from this client's canvas (we erased it locally
          // before sending). Showing a "denied" toast would confuse
          // the user ("you don't have permission?"), and rolling back
          // would bring the stroke back on screen. Just drop it silently.
          if (msg.action === 'object:commit' && msg.reason === 'That object was deleted') {
            // Ensure it's gone from local state (belt-and-suspenders —
            // _localDelete already removed it, but a reconnect or
            // out-of-order processing might have re-added it).
            if (msg.id && objectsById.has(msg.id)) {
              objectsById.delete(msg.id)
              pushObjects()
            }
            return
          }

          push({ deniedReason: msg.reason })
          setTimeout(() => push({ deniedReason: null }), 3000)
          // FIX: without this, a denied erase or draw stayed erased
          // or drawn only on the denied user's own screen forever —
          // the optimistic local change (_localDelete / _localCommit,
          // both applied before the server's decision came back) was
          // never undone, so this client's board silently diverged
          // from the server's canonical state and from what the
          // other participant actually sees. Roll back the specific
          // action the server identified (see whiteboard.signaling.js).
          if (msg.action === 'object:delete' && msg.objectId) {
            const restored = pendingDeletes.get(msg.objectId)
            console.log('[wb-client] object:delete denied — rolling back?', {
              objectId: msg.objectId, hadPendingSnapshot: !!restored,
            })
            if (restored) {
              objectsById.set(msg.objectId, restored)
              pendingDeletes.delete(msg.objectId)
              pushObjects()
            }
          } else if (msg.action === 'object:commit' && msg.id) {
            objectsById.delete(msg.id)
            pushObjects()
          }
          return

        // ── Pages ───────────────────────────────────────────
        case 'page:switch':
          push({ currentPageIndex: msg.pageIndex })
          return

        case 'page:added':
          // Server sends the new page serialized identically to snapshot pages:
          // { id, index, backgroundImageUrl }
          push({
            pages:            [...state.pages, msg.page],
            currentPageIndex: msg.currentPageIndex,
          })
          return

        case 'page:deleted':
          // Server re-indexes all remaining pages and returns the full
          // updated list — replace entirely, never splice locally.
          push({
            pages:            msg.pages,
            currentPageIndex: msg.currentPageIndex,
          })
          return

        case 'pdf:pages-added':
          push({ pages: [...state.pages, ...msg.pages] })
          return

        default:
          return
      }
    }

    ws.onerror = () => { /* onclose always follows */ }

    ws.onclose = (event) => {
      if (tornDown) return
      // 4000 = replaced by a new connection — do nothing
      if (event.code === 4000) return
      // 4001/4003 = auth failure — do not retry
      if (event.code === 4001 || event.code === 4003) {
        push({ connectionState: 'failed' })
        return
      }
      // FIX: 1012 (service restart, sent on deploy/SIGTERM) and 1013
      // (server at capacity, or this socket fell too far behind and
      // was shed for backpressure) are deliberate, expected
      // server-initiated closes — not failures. Reconnect quickly
      // and don't spend a retry attempt on them, otherwise a normal
      // deploy burns through the budget and lands the user on the
      // permanent 'failed' state for what is a ~1s interruption.
      if (event.code === 1012 || event.code === 1013) {
        push({ connectionState: 'connecting' })
        reconnectTimer = setTimeout(connect, 500 + Math.floor(Math.random() * 1000))
        return
      }
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        push({ connectionState: 'failed' })
        return
      }
      reconnectAttempts += 1
      reconnectTimer = setTimeout(connect, Math.min(1000 * reconnectAttempts, 5000))
    }
  }

  connect()

  // ── Public API ────────────────────────────────────────────

  const previewObject = (id, pageId, tool, color, strokeWidth, geometry) =>
    send({ type: 'object:preview', id, pageId, tool, color, strokeWidth, geometry })

  const commitObject = (id, pageId, tool, color, strokeWidth, geometry) =>
    send({ type: 'object:commit', id, pageId, tool, color, strokeWidth, geometry })

  const deleteObject = (objectId) =>
    send({ type: 'object:delete', objectId })

  // BUG FIX — RACE-FREE ERASE HIT-TESTING: Whiteboard.jsx's eraser
  // used to hit-test against a React ref that only updates when the
  // component re-renders and its useMemo recomputes from wb.objects.
  // That recompute always happens *after* onState() fires, so there
  // is a real window — one render cycle wide — where an object has
  // already been added to objectsById (durable, visible on screen)
  // but hasn't yet reached that ref. Erasing inside that window found
  // nothing and silently no-opped. This affected the LOCAL author's
  // own just-drawn stroke (fixed once by patching the ref manually
  // at commit time) but just as much any stroke arriving from the
  // OTHER participant over the socket — same lag, no local patch
  // covers it. getObjectsSnapshot() reads objectsById directly, so
  // it is always exactly current, regardless of whether React has
  // rendered yet.
  const getObjectsSnapshot = () => Array.from(objectsById.values())

  const raiseHand  = ()           => send({ type: 'hand:raise' })
  const lowerHand  = ()           => send({ type: 'hand:lower' })
  const grantHand  = (studentId)  => send({ type: 'hand:grant', studentId })
  const setDrawMode = (mode)      => send({ type: 'permission:change', mode })

  // Page actions — update the sender's own state immediately
  // (the server's subscriber skips the sender's socket when
  // broadcasting, so without this the sender never sees their
  // own page switch/add/delete reflected in the UI).
  const switchPage = (pageIndex) => {
    send({ type: 'page:switch', pageIndex })
    // Optimistic: update sender immediately
    push({ currentPageIndex: pageIndex })
  }

  const addPage = () => {
    send({ type: 'page:add' })
    // The server will broadcast page:added with the real page object
    // and new index. We can't optimistically add here because we don't
    // know the new page's server-generated id yet — the broadcast will
    // arrive shortly and update both sides. The sender will see a brief
    // delay (one round-trip) which is acceptable.
    // NOTE: to make the sender see it too, we echo the broadcast back
    // to ourselves via the server — remove the authorId skip for
    // page:added/page:deleted in the signaling server, OR handle it
    // optimistically here once the server returns the page id.
    // For now the server must echo back to the sender for these two.
  }

  const deletePage = (pageIndex) => {
    send({ type: 'page:delete', pageIndex })
    // Same as addPage — we need the re-indexed pages list from the
    // server. The signaling server must echo back to the sender.
  }

  // Local-only optimistic updates (used by Whiteboard.jsx for
  // immediate feedback before the server echo arrives)
  const _localCommit = (obj) => {
    objectsById.set(obj.id, obj)
    pushObjects()
  }

  const _localDelete = (objectId) => {
    // FIX: snapshot the object before removing it, so a later
    // permission:denied for this exact delete can restore it (see
    // the permission:denied case above). This is the only point
    // that still has the object in hand — by the time deleteObject()
    // below runs, it's already gone from objectsById.
    const obj = objectsById.get(objectId)
    console.log('[wb-client] _localDelete', { objectId, found: !!obj })
    if (obj) {
      pendingDeletes.set(objectId, obj)
      // If no denial arrives for this delete within a few seconds,
      // treat it as confirmed and release the snapshot — otherwise
      // pendingDeletes would grow for the entire session.
      setTimeout(() => pendingDeletes.delete(objectId), 5000)
    }
    objectsById.delete(objectId)
    pushObjects()
  }

  const setPreviewCallback = (cb) => { onPreviewCallback = cb }

  // ── Shared viewport ───────────────────────────────────────
  // Delivered through a callback rather than through push()/React
  // state for the same reason previews are: a two-finger drag emits
  // these at animation-frame rate, and routing every one of them
  // through a setState would re-render the whole board on each
  // frame. Whiteboard.jsx applies them straight to its panRef.
  const setViewportCallback = (cb) => { onViewportCallback = cb }

  // No optimistic echo here, unlike switchPage above: the server's
  // subscriber skips the sender's own socket when broadcasting, so
  // the mover already has the pan applied locally and would only be
  // fighting itself if this wrote back.
  const sendViewport = ({ scrollX, scrollY, zoom = 1 }) =>
    send({ type: 'viewport:sync', scrollX, scrollY, zoom })

  const close = () => {
    tornDown = true
    if (reconnectTimer) clearTimeout(reconnectTimer)
    if (ws) {
      ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null
      try { ws.close(1000, 'Client closed whiteboard') } catch { /* ignore */ }
    }
    ws = null
  }

  return {
    previewObject,
    commitObject,
    deleteObject,
    getObjectsSnapshot,
    raiseHand,
    lowerHand,
    grantHand,
    setDrawMode,
    switchPage,
    addPage,
    deletePage,
    _localCommit,
    _localDelete,
    setPreviewCallback,
    setViewportCallback,
    sendViewport,
    close,
  }
}